import type { JSX } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Col } from "@gss/components/Col";
import type { NavMenuNode } from "@gss/components/NavMenu";
import { NavMenu } from "@gss/components/NavMenu";
import { Row } from "@gss/components/Row";
import type { DimensionValue } from "@gss/types/Dimension";
import type {
    MeasurementStat,
    MeasurementStatDimension,
    MeasurementStatQuestion,
} from "@gss/types/measurementStat";
import type { LanguageTranslation } from "../../translations/translation.types";
import type { FilterSerie } from "../filters/filters.types";
import { useTranslation } from "../util/Translation";
import { Dimension, filterDimensionQuestions } from "./Dimension";
import { DimensionScores } from "./DimensionScores";
import {
    filterOverviewQuestions,
    QuestionOverviewTotal,
} from "./QuestionOverviewTotal";
import "./ResultGraphs.css";
import { ResponseRate } from "./ResponseRate";

interface Props {
    stat: MeasurementStat;
    questions: MeasurementStatQuestion[];
    subtitle: string;
    filterSeries: FilterSerie[];
}

interface MeasurementStatDimensionWithLabel extends MeasurementStatDimension {
    label: string;
}

type Tab =
    | {
          type: "dimensionScores" | "questionOverview" | "responseRate";
          text: string;
      }
    | {
          type: "dimension";
          text: string;
          dim: MeasurementStatDimension;
      }
    | { type: "divider" };

export function ResultGraphs({
    stat,
    questions,
    subtitle,
    filterSeries,
}: Props): JSX.Element | null {
    const { translation, translateDimension } = useTranslation();
    const [currentTab, setCurrentTab] = useState<Tab>();
    const dimensions = useMemo(
        () => getDimensions(translation, translateDimension, stat),
        [stat, translateDimension, translation],
    );
    const scoreDimensions = useMemo(
        () => dimensions.filter((d) => d.hasScores),
        [dimensions],
    );
    const otherDimensions = useMemo(
        () => dimensions.filter((d) => !d.hasScores),
        [dimensions],
    );
    const tabs = useMemo(
        () => getTabs(translation, scoreDimensions, otherDimensions),
        [translation, scoreDimensions, otherDimensions],
    );

    useEffect(() => {
        setCurrentTab(tabs[0]);
    }, [tabs]);

    if (currentTab == null) {
        return null;
    }

    const nodes = tabs.map((tab): NavMenuNode => {
        if (tab.type === "divider") {
            return {
                hr: true,
            };
        }
        return {
            children: tab.text,
            onClick: () => setCurrentTab(tab),
        };
    });

    const activeNodeIndex = tabs.findIndex((tab) => {
        if (tab.type === "divider") {
            return false;
        }
        if (tab.type === "dimension" && currentTab.type === "dimension") {
            return tab.dim.name === currentTab.dim.name;
        }
        return tab.type === currentTab.type;
    });
    const activeNode =
        activeNodeIndex !== -1 ? nodes[activeNodeIndex] : nodes[0];

    const renderCurrentTab = () => {
        switch (currentTab.type) {
            case "dimensionScores":
                return (
                    <DimensionScores
                        stat={stat}
                        dimensions={scoreDimensions}
                        subtitle={subtitle}
                        filterSeries={filterSeries}
                    />
                );
            case "responseRate":
                return (
                    <ResponseRate
                        stat={stat}
                        subtitle={subtitle}
                        filterSeries={filterSeries}
                    />
                );
            case "questionOverview": {
                return (
                    <QuestionOverviewTotal
                        stat={stat}
                        questions={filterOverviewQuestions(
                            questions,
                            scoreDimensions,
                        )}
                        subtitle={subtitle}
                        filterSeries={filterSeries}
                    />
                );
            }
            case "dimension":
                return (
                    <Dimension
                        key={currentTab.dim.name}
                        stat={stat}
                        dimension={currentTab.dim}
                        questions={filterDimensionQuestions(
                            questions,
                            currentTab.dim,
                        )}
                        subtitle={subtitle}
                        filterSeries={filterSeries}
                    />
                );
            case "divider":
                throw new Error("Divider cannot be selected");
            default: {
                const _ensureExhaustive: never = currentTab;
                throw new Error("Unknown tab value");
            }
        }
    };

    return (
        <Row>
            <Col xs="auto">
                <NavMenu
                    variant="tabs"
                    vertical
                    className="h-100"
                    activeNode={activeNode}
                >
                    {nodes}
                </NavMenu>
            </Col>
            <Col>{renderCurrentTab()}</Col>
        </Row>
    );
}

function getDimensions(
    t: LanguageTranslation,
    translateDimension: (dim: DimensionValue) => string,
    stat: MeasurementStat,
): MeasurementStatDimensionWithLabel[] {
    const { dimensions } = stat;
    // If there is only one dimension and that is "other": replace the name with "questions" instead
    if (dimensions.length === 1 && dimensions[0].name === "Övrigt") {
        return [
            {
                ...dimensions[0],
                label: t.stats.questions,
            },
        ];
    }
    return dimensions.map((d) => ({
        ...d,
        label: translateDimension(d.name),
    }));
}

function getTabs(
    translation: LanguageTranslation,
    scoreDimensions: MeasurementStatDimensionWithLabel[],
    otherDimensions: MeasurementStatDimensionWithLabel[],
): Tab[] {
    const t = translation.stats;
    const tabs: Tab[] = [];

    if (scoreDimensions.length > 0) {
        tabs.push(
            { type: "dimensionScores", text: t.dimensionScores },
            { type: "questionOverview", text: t.questionOverview },
        );
    }

    tabs.push({ type: "responseRate", text: t.responseRate });

    if (scoreDimensions.length > 0) {
        tabs.push({ type: "divider" });

        for (const d of scoreDimensions) {
            tabs.push({
                type: "dimension",
                text: d.label,
                dim: d,
            });
        }
    }

    if (otherDimensions.length > 0) {
        tabs.push({ type: "divider" });

        for (const d of otherDimensions) {
            tabs.push({
                type: "dimension",
                text: d.label,
                dim: d,
            });
        }
    }

    return tabs;
}
